import _ from "lodash";
import React, { PureComponent, useState, useEffect, useRef } from "react";
import { compose } from "lib/apolloClient";
import classes from "./SinglePointRubricTable.scss";
import TextAreaInput from "UIComponents/TextAreaInput";
import I18nHOC from "UIComponents/I18nHOC";
import { colors, fontStyle } from "Constants";
import { connect } from "react-redux";
import classNames from "classnames";
import { updateField } from "modules/FieldUpdateThunks";
import {
  writeSinglePointRubricElementLocal,
  createSinglePointRubricStandard,
  deleteSinglePointRubricStandard,
} from "SinglePointRubric/modules/SinglePointRubricModule";
import { Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  AddOutlined,
  DeleteOutlined,
  AssessmentApplicableOutlined,
  SinglePointRubricOutlined,
} from "@toddle-edu/ds-icons";
import { updateEvaluationData } from "ClassRoom/modules/ClassRoomModule";
import SinglePointRubricWithReordering from "../SinglePointRubricWithReordering";
import { saveAssessmentToolUpdateStatusInRedux } from "Assessments/modules/AssessmentModule";
import { getAssessmentToolStyles } from "ClassRoom/modules/utils";
import SkeletonLoader from "UIComponents/SkeletonLoader";

const textAreaStyles = {
  cellTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: "0 24px 0 0",
    color: colors.textDefault,
    lineHeight: 1.57,
    fontSize: "1.6rem",
    width: "100%",
    ...fontStyle.bold,
    overflow: "hidden",
  },
  headerCellTitle: {
    lineHeight: 1.5,
    fontSize: "1.6rem",
    padding: 0,
    color: "var(--text-primary)",
  },
  cellSubTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.textSubtle,
    lineHeight: 1.57,
    fontSize: "1.6rem",
    width: "100%",
    ...fontStyle.medium,
  },
  headerSubCellTitle: {
    display: "flex",
    color: "var(--text-primary)",
  },
  cellResponse: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.textDefault,
    lineHeight: 1.57,
    letterSpacing: "-0.1px",
    fontSize: "1.6rem",
    width: "100%",
    ...fontStyle.medium,
  },
  cellResponseV2: {
    fontSize: "1.4rem",
    color: "var(--text-primary)",
  },
  aiEvaluationInput: {
    cursor: "pointer",
  },
};

const splitViewTextAreaStyles = {
  cellTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: "var(--text-primary)",
    lineHeight: "2rem",
    fontSize: "1.4rem",
    width: "100%",
    ...fontStyle.demiBold,
    overflow: "hidden",
  },
  evaluationCellTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.black,
    lineHeight: "2rem",
    fontSize: "1.4rem",
    width: "100%",
    ...fontStyle.demiBold,
    overflow: "hidden",
  },
  cellSubTitle: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: "var(--text-primary)",
    lineHeight: "1.6rem",
    fontSize: "1.4rem",
    width: "100%",
    ...fontStyle.medium,
  },
  cellResponse: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: 0,
    color: colors.textDefault,
    lineHeight: "2rem",
    letterSpacing: "-0.1px",
    fontSize: "1.4rem",
    width: "100%",
    ...fontStyle.medium,
  },
  inputStyle: {
    alignItems: "center",
    justifyContent: "center",
    fontWeight: fontStyle.medium,
    fontSize: "1.4rem",
    color: colors.textDisabled,
  },
};

const styles = {
  inputStyle: {
    alignItems: "center",
    justifyContent: "center",
    fontWeight: fontStyle.medium,
    fontSize: "1.6rem",
    color: colors.textDisabled,
  },
  containerStyle: {
    height: "100%",
  },
  textAreaInputStyles: {
    flex: 1,
  },
  starsTwoFilledIconStyle: {
    color: "var(--interactive-tertiary)",
  },
};

const Header = props => {
  const {
    t,
    criteriaObj,
    cellStyle = {},
    rowHeaderCellStyle = {},
    mode,
    isSplitView = false,
    headerCellTitleStyle = {},
    headerSubCellTitleStyle = {},
    subtitleContainerStyle = {},
    subtitleContainerStyleV2 = {},
    isEvaluation,
    inFocusCell,
    updateInputRef,
    setFocusedCell,
    writeSinglePointRubricElement,
    writeSinglePointRubricElementLocal,
    saveSinglePointRubricUpdateActivityStatus,
    testId,
    showUpdatedDesign = false,
    isTopLeftCell,
    isTopRightCell,
    topLeftCellStyle = {},
    topRightCellStyle = {},
  } = props;

  const [title, setTitle] = useState(criteriaObj?.title);
  const [subTitle, setSubTitle] = useState(criteriaObj?.subTitle);

  const refTitleKey = `Header_${criteriaObj.id}_Title`;
  const refSubtitleKey = `Header_${criteriaObj.id}_Subtitle`;

  const disabled = _.includes(criteriaObj.id, "NEW");

  const updateInputFieldTitle = params => {
    setTitle(params.title);
    writeSinglePointRubricElement({
      id: criteriaObj.id,
      params,
      type: "CRITERIA",
      elementDetails: criteriaObj,
      isTitle: true,
    });
    saveSinglePointRubricUpdateActivityStatus();
  };

  const updateInputFieldSubtitle = params => {
    setSubTitle(params.subTitle);
    writeSinglePointRubricElement({
      id: criteriaObj.id,
      params,
      type: "CRITERIA",
      elementDetails: criteriaObj,
      isTitle: false,
    });
    saveSinglePointRubricUpdateActivityStatus();
  };

  const cellClass = classNames({
    [classes.rowHeaderCell]: true,
    [classes.focusedCell]: !isEvaluation && inFocusCell == criteriaObj.id,
    [classes.evaluationCell]: isEvaluation,
    [classes.disabled]: disabled,
    [classes.rowHeaderCellV2]: showUpdatedDesign,
  });

  const styles = {
    ...cellStyle,
    ...rowHeaderCellStyle,
    ...(isTopLeftCell ? topLeftCellStyle : {}),
    ...(isTopRightCell ? topRightCellStyle : {}),
  };

  return (
    <div className={classes.pseudoColumn} key={criteriaObj.id}>
      <div
        className={cellClass}
        style={styles}
        onFocus={() => setFocusedCell(criteriaObj.id)}
      >
        <TextAreaInput
          value={title}
          mode={mode}
          textAreaStyles={{
            ...textAreaStyles.cellTitle,
            ...textAreaStyles.headerCellTitle,
            ...(isSplitView ? splitViewTextAreaStyles.cellTitle : {}),
            ...headerCellTitleStyle,
          }}
          updateInputRef={ref => updateInputRef({ ref, refKey: refTitleKey })}
          onBlurInputField={() => {
            setFocusedCell(null);
            writeSinglePointRubricElementLocal({
              id: criteriaObj.id,
              params: { title },
              type: "CRITERIA",
              elementDetails: criteriaObj,
              isTitle: true,
            });
          }}
          onFocusInputField={() => setFocusedCell(refTitleKey)}
          disabled={disabled}
          minRows={1}
          emptyText={""}
          name={"title"}
          placeholder={t("common:click_to_add_withLabel", {
            label: t("common:standard"),
          })}
          updateInputField={updateInputFieldTitle}
          className={classes.placeholderTextOnSubtle}
          testId={`${testId}-singleRubricTable-cell-${criteriaObj?.id}-add`}
        />
        <TextAreaInput
          value={subTitle}
          mode={mode}
          textAreaStyles={{
            ...textAreaStyles.cellSubTitle,
            ...textAreaStyles.headerSubCellTitle,
            ...(isSplitView ? splitViewTextAreaStyles.cellSubTitle : {}),
            ...headerSubCellTitleStyle,
          }}
          updateInputRef={ref =>
            updateInputRef({ ref, refKey: refSubtitleKey })
          }
          onBlurInputField={() => {
            setFocusedCell(null);
            writeSinglePointRubricElementLocal({
              id: criteriaObj.id,
              params: { subTitle },
              type: "CRITERIA",
              elementDetails: criteriaObj,
              isTitle: false,
            });
          }}
          onFocusInputField={() => setFocusedCell(refSubtitleKey)}
          containerStyle={{
            marginTop: 4,
            ...subtitleContainerStyle,
            ...subtitleContainerStyleV2,
          }}
          disabled={disabled}
          minRows={1}
          name={"subTitle"}
          emptyText={""}
          placeholder={t("common:definition")}
          updateInputField={updateInputFieldSubtitle}
          className={classes.placeholderColor}
          testId={`${testId}-singleRubricTable-cell-${criteriaObj?.id}-definition`}
        />
      </div>
    </div>
  );
};

const Columns = props => {
  const {
    t,
    mode,
    rubric,
    criteriaObj,
    standardObj,
    isEvaluation,
    isSplitView = false,
    isSplitViewEnabled,
    inFocusCell,
    cellStyle,
    setFocusedCell,
    updateInputRef,
    getNonCriteriaEvaluationCell,
    writeSinglePointRubricElement,
    deleteSinglePointRubricStandard,
    writeSinglePointRubricElementLocal,
    saveSinglePointRubricUpdateActivityStatus,
    testId,
    showUpdatedDesign = false,
    isFirstColumn,
    isLastColumn,
    isLastRow,
    criteriaCellStyle = {},
    nonCriteriaCellStyle = {},
    firstColumnCellStyle = {},
    lastColumnCellStyle = {},
    bottomLeftCellStyle = {},
    bottomRightCellStyle = {},
    criteriaCellTitleStyle = {},
    criteriaCellSubTitleStyle = {},
    subtitleContainerStyleV2 = {},
    isAiEvaluationEnabled,
    toolEvaluationResponses,
    isGeneratingAIResponse,
  } = props;

  const [title, setTitle] = useState(standardObj?.title);
  const [subTitle, setSubTitle] = useState(standardObj?.subTitle);

  const standards = _.get(rubric, "standards", []);
  const isCriteriaStandardColumn = criteriaObj.isCriteriaStandardColumn;

  const refTitleKey = `Row_${standardObj.id}_Title`;
  const refSubtitleKey = `Row_${standardObj.id}_Subtitle`;
  const disabled = _.includes(standardObj.id, "NEW");

  const applyContainerStyle =
    mode === "view" &&
    _.isEmpty(standardObj?.title) &&
    _.isEmpty(standardObj?.subTitle);

  const inputStyle = applyContainerStyle
    ? isSplitView
      ? splitViewTextAreaStyles.inputStyle
      : styles.inputStyle
    : {};

  const updateInputFieldTitle = params => {
    setTitle(params.title);
    writeSinglePointRubricElement({
      id: standardObj.id,
      params,
      type: "STANDARD",
      elementDetails: standardObj,
      isTitle: true,
    });
    saveSinglePointRubricUpdateActivityStatus();
  };

  const updateInputFieldSubtitle = params => {
    setSubTitle(params.subTitle);
    writeSinglePointRubricElement({
      id: standardObj.id,
      params,
      type: "STANDARD",
      elementDetails: standardObj,
      isTitle: false,
    });
    saveSinglePointRubricUpdateActivityStatus();
  };

  const aiEvaluation = _.find(toolEvaluationResponses, {
    rowId: standardObj.id,
    columnId: criteriaObj.id,
    creatorType: "AI",
    isDeleted: false,
  });

  const aiEvaluationText = _.get(aiEvaluation, "subjectiveResponse", "");

  const cellClass = classNames({
    [classes.cell]: true,
    [classes.focusedCell]:
      !isEvaluation &&
      inFocusCell == standardObj.id &&
      isCriteriaStandardColumn,
    [classes.evaluationCell]: isEvaluation,
    [classes.evaluationCellSplitView]: isEvaluation && isSplitViewEnabled,
    [classes.nonCriteriaCell]: !isCriteriaStandardColumn,
    [classes.splitViewNonCriteriaCell]:
      !isCriteriaStandardColumn && isSplitView,
    [classes.nonCriteriaEvaluationCell]:
      isEvaluation && !isCriteriaStandardColumn,
    [classes.disabled]: disabled,
    [classes.cellV2]: showUpdatedDesign,
    [classes.aiEvaluationCell]:
      !_.isEmpty(aiEvaluationText) &&
      isAiEvaluationEnabled &&
      !isSplitView &&
      !isCriteriaStandardColumn &&
      !isGeneratingAIResponse,
    [classes.aiGeneratingResponseCell]:
      !isSplitView && !isCriteriaStandardColumn,
  });

  const cellStyles = {
    ...cellStyle,
    ...(isCriteriaStandardColumn ? criteriaCellStyle : {}),
    ...(!isCriteriaStandardColumn ? nonCriteriaCellStyle : {}),
    ...(isFirstColumn ? firstColumnCellStyle : {}),
    ...(isLastColumn ? lastColumnCellStyle : {}),
    ...(isLastRow && isFirstColumn ? bottomLeftCellStyle : {}),
    ...(isLastRow && isLastColumn ? bottomRightCellStyle : {}),
  };

  return (
    <div
      className={cellClass}
      key={criteriaObj.id}
      style={cellStyles}
      onFocus={() => setFocusedCell(standardObj.id)}
    >
      {isCriteriaStandardColumn ? (
        <React.Fragment>
          <TextAreaInput
            value={title}
            textAreaStyles={{
              ...textAreaStyles.cellTitle,
              ...(isSplitView
                ? splitViewTextAreaStyles.evaluationCellTitle
                : {}),
              ...criteriaCellTitleStyle,
            }}
            updateInputRef={ref => updateInputRef({ ref, refKey: refTitleKey })}
            onBlurInputField={() => {
              setFocusedCell(null);
              writeSinglePointRubricElementLocal({
                id: standardObj.id,
                params: { title },
                type: "STANDARD",
                elementDetails: standardObj,
                isTitle: true,
              });
            }}
            onFocusInputField={() => setFocusedCell(refTitleKey)}
            minRows={1}
            emptyText={applyContainerStyle ? t("common:no_criteria_added") : ""}
            inputStyle={inputStyle}
            containerStyle={applyContainerStyle ? styles.containerStyle : {}}
            disabled={disabled}
            mode={mode}
            placeholder={t("unitPlan:enter_criteria_title")}
            name={"title"}
            updateInputField={updateInputFieldTitle}
            className={classes.placeholderColor}
            testId={`${testId}-singleRubricTable-cell-${standardObj?.id}-criteriaTitle`}
          />
          <TextAreaInput
            value={subTitle}
            textAreaStyles={{
              ...textAreaStyles.cellSubTitle,
              ...(isSplitView
                ? {
                    ...splitViewTextAreaStyles.cellSubTitle,
                    color: colors.textDefault,
                  }
                : {}),
              ...criteriaCellSubTitleStyle,
            }}
            updateInputRef={ref =>
              updateInputRef({ ref, refKey: refSubtitleKey })
            }
            onBlurInputField={() => {
              setFocusedCell(null);
              writeSinglePointRubricElementLocal({
                id: standardObj.id,
                params: { subTitle },
                type: "STANDARD",
                elementDetails: standardObj,
                isTitle: false,
              });
            }}
            onFocusInputField={() => setFocusedCell(refSubtitleKey)}
            minRows={1}
            containerStyle={{ marginTop: 8, ...subtitleContainerStyleV2 }}
            emptyText={""}
            disabled={disabled}
            mode={mode}
            placeholder={t("common:click_to_add_withLabel", {
              label: t("common:definition"),
            })}
            name={"subTitle"}
            updateInputField={updateInputFieldSubtitle}
            className={classes.placeholderColor}
            testId={`${testId}-singleRubricTable-cell-${standardObj?.id}-add`}
          />

          {mode !== "view" && _.size(standards) > 1 && (
            <div className={classes.actionButton}>
              <Tooltip
                tooltip={t("common:delete_with_label", {
                  label: t("common:row"),
                })}
                showArrow={true}
              >
                <IconButton
                  dsVersion="2.0"
                  icon={<DeleteOutlined variant="subtle" />}
                  variant="neutral"
                  type="plain"
                  size={"x-small"}
                  onClick={() => {
                    deleteSinglePointRubricStandard(standardObj?.id);
                  }}
                  disabled={_.includes(standardObj?.id, "NEW")}
                  testId={`${testId}-singleRubricTable-cell-${standardObj?.id}-delete`}
                />
              </Tooltip>
            </div>
          )}
        </React.Fragment>
      ) : isGeneratingAIResponse && !isSplitView ? (
        <SkeletonLoader height="100%" width="100%" />
      ) : (
        isEvaluation &&
        getNonCriteriaEvaluationCell({
          criteriaObj,
          standardObj,
          aiEvaluationText,
        })
      )}
    </div>
  );
};

const NonCriteriaEvaluationCell = props => {
  const {
    t,
    rubric,
    userType,
    isEvaluationEnable,
    standardObj,
    criteriaObj,
    isEvaluatedByStudent,
    updateEvaluationCell,
    isSplitView = false,
    updateEvaluationData,
    evaluationCellRefs,
    toolEvaluationResponses,
    showRevampedEvaluationUI = false,
    evaluationCellContainerStyle = {},
    isAiEvaluationEnabled,
    onAcceptAIEvaluation,
    isGeneratingAIResponse,
    aiEvaluationText,
  } = props;

  const isUserActionRef = useRef(false);

  const _teacherEvaluation = _.get(
    _.find(
      toolEvaluationResponses,
      response =>
        response.rowId == standardObj.id &&
        response.columnId == criteriaObj.id &&
        response.creatorType == "STAFF"
    ),
    "subjectiveResponse",
    ""
  );

  const _studentEvaluation = _.get(
    _.find(
      toolEvaluationResponses,
      response =>
        response.rowId == standardObj.id &&
        response.columnId == criteriaObj.id &&
        response.creatorType == "STUDENT"
    ),
    "subjectiveResponse",
    ""
  );

  const [teacherEvaluation, setTeacherEvaluation] = useState(
    _teacherEvaluation
  );
  const [studentEvaluation, setStudentEvaluation] = useState(
    _studentEvaluation
  );

  useEffect(() => {
    if (!_.isEqual(teacherEvaluation, _teacherEvaluation)) {
      setTeacherEvaluation(_teacherEvaluation);
    }
  }, [_teacherEvaluation, toolEvaluationResponses]);

  useEffect(() => {
    if (!_.isEqual(studentEvaluation, _studentEvaluation)) {
      setStudentEvaluation(_studentEvaluation);
    }
  }, [_studentEvaluation, toolEvaluationResponses]);

  const standardId = _.get(standardObj, "id");
  const criteriaId = _.get(criteriaObj, "id");

  const refKey = `${criteriaId}_${standardId}`;

  const teacherMode =
    isEvaluationEnable && userType == "staff" ? "edit" : "view";
  const studentMode =
    isEvaluationEnable && userType == "student" ? "edit" : "view";

  const inputValue =
    isAiEvaluationEnabled && !_.isEmpty(aiEvaluationText)
      ? aiEvaluationText
      : teacherEvaluation;

  const onKeyDown = ({ e, standardId, criteriaId }) => {
    // focus on next input on tab press
    if (e?.key === "Tab") {
      const criterias = _.sortBy(rubric?.criterias, "id");
      const standards = _.sortBy(rubric?.standards, "id");

      const lastCriteriaColumnId = _.findLast(criterias, {
        isCriteriaStandardColumn: false,
      })?.id;

      const nextCriteria = _.find(criterias, criteria => {
        return (
          _.get(criteria, "id") !== criteriaId &&
          !_.get(criteria, "isCriteriaStandardColumn")
        );
      })?.id;

      let key = "";

      if (criteriaId === lastCriteriaColumnId) {
        const currentStandardIndex = _.findIndex(standards, standard => {
          return _.get(standard, "id") === standardId;
        });

        const nextStandard =
          standards[(currentStandardIndex + 1) % _.size(standards)]?.id;

        key = `${nextCriteria}_${nextStandard ?? standardId}`;
      } else {
        key = `${nextCriteria}_${standardId}`;
      }

      evaluationCellRefs?.[key]?.focus();
    }
  };

  const updateTeacherEvaluation = ({ value, creatorType = "STAFF" }) => {
    setTeacherEvaluation(value);
    updateEvaluationCell?.({
      subjectiveResponse: value,
      rowId: standardId,
      columnId: criteriaId,
      isDebounce: true,
      creatorType,
    });
  };

  const updateStudentEvaluation = value => {
    setStudentEvaluation(value);
    updateEvaluationCell?.({
      subjectiveResponse: value,
      rowId: standardId,
      columnId: criteriaId,
      isDebounce: true,
    });
  };

  const teacherEvaluationCellClass = classNames({
    [classes.evaluationEditCell]: true,
    [classes.splitViewEvaluationEditCell]: isSplitView,
    [classes.evaluationViewCell]: !isEvaluationEnable || userType == "student",
    [classes.emptyEvaluationCell]:
      isEvaluationEnable && !_.isEmpty(teacherEvaluation),
    [classes.evaluationEditCellWithBorder]: showRevampedEvaluationUI,
    [classes.selectedCell]:
      showRevampedEvaluationUI && !_.isEmpty(teacherEvaluation),
    [classes.aiEvaluationInnerCell]:
      (!_.isEmpty(aiEvaluationText) || isGeneratingAIResponse) &&
      isAiEvaluationEnabled &&
      !isSplitView,
    [classes.aiEvaluationCell]:
      (!_.isEmpty(aiEvaluationText) || isGeneratingAIResponse) &&
      isAiEvaluationEnabled &&
      isSplitView,
  });

  const studentEvaluationCellClass = classNames({
    [classes.evaluationEditCell]: true,
    [classes.splitViewEvaluationEditCell]: isSplitView,
    [classes.evaluationViewCell]: !isEvaluationEnable || userType == "staff",
    [classes.studentEvaluationCell]:
      isEvaluatedByStudent && !showRevampedEvaluationUI,
    [classes.emptyEvaluationCell]:
      isEvaluationEnable && !_.isEmpty(studentEvaluation),
    [classes.evaluationEditCellWithBorder]: showRevampedEvaluationUI,
    [classes.selectedStudentCell]:
      showRevampedEvaluationUI && !isEvaluationEnable,
  });

  const evalutionCellContainer = classNames({
    [classes.evaluationCellContainer]: true,
    [classes.splitViewEvaluationCellContainer]: isSplitView,
    [classes.aiGradingContainer]: isAiEvaluationEnabled,
  });

  const showTickSvg = !isAiEvaluationEnabled
    ? !_.isEmpty(teacherEvaluation)
    : !_.isEmpty(teacherEvaluation) && _.isEmpty(aiEvaluationText);

  const aiColumnStyle = {
    ...(isAiEvaluationEnabled && !_.isEmpty(aiEvaluationText)
      ? textAreaStyles.aiEvaluationInput
      : {}),
  };

  const onFocusCell = () => {
    updateEvaluationData?.({ isTextFocus: true });
    if (
      isUserActionRef.current &&
      !_.isEmpty(aiEvaluationText) &&
      isAiEvaluationEnabled
    ) {
      onAcceptAIEvaluation({
        rowId: standardId,
        columnId: criteriaId,
        isDeleteAiRatings: true,
        inputText: aiEvaluationText,
      });
    }

    isUserActionRef.current = false;
  };

  return (
    <div
      className={evalutionCellContainer}
      style={evaluationCellContainerStyle}
    >
      {(!showRevampedEvaluationUI || !_.isEmpty(teacherEvaluation)) &&
      !isGeneratingAIResponse ? (
        <div
          className={teacherEvaluationCellClass}
          style={aiColumnStyle}
          onMouseDown={() => (isUserActionRef.current = true)}
          onClick={onFocusCell}
        >
          <div
            className={showTickSvg ? classes.tickSvgComponent : null}
            style={{ backgroundColor: colors.blue400 }}
          />
          <div className={classes.inputContainer}>
            <TextAreaInput
              value={inputValue}
              textAreaStyles={{
                ...textAreaStyles.cellResponse,
                ...(isSplitView ? splitViewTextAreaStyles.cellResponse : {}),
                ...(showRevampedEvaluationUI
                  ? textAreaStyles.cellResponseV2
                  : {}),
                ...(isAiEvaluationEnabled && !_.isEmpty(aiEvaluationText)
                  ? textAreaStyles.aiEvaluationInput
                  : {}),
              }}
              updateInputField={value => updateTeacherEvaluation({ value })}
              updateInputRef={ref => {
                if (teacherMode === "edit") {
                  evaluationCellRefs[refKey] = ref;
                }
              }}
              minRows={2}
              emptyText={"-"}
              mode={teacherMode}
              placeholder={t("common:typeHere")}
              className={classes.placeholderColor}
              onBlur={() => {
                updateEvaluationData?.({ isTextFocus: false });
              }}
              onFocus={onFocusCell}
              onKeyDown={e => onKeyDown({ e, standardId, criteriaId })}
              containerStyle={
                isAiEvaluationEnabled ? styles.textAreaInputStyles : {}
              }
              onMouseDown={() => (isUserActionRef.current = true)}
            />
          </div>
        </div>
      ) : isGeneratingAIResponse ? (
        <SkeletonLoader />
      ) : null}

      {isEvaluatedByStudent && (
        <div className={studentEvaluationCellClass}>
          {!showRevampedEvaluationUI || !isEvaluationEnable ? (
            <div
              className={classes.tickSvgComponent}
              style={{ backgroundColor: colors.neutral200 }}
            ></div>
          ) : null}
          <TextAreaInput
            value={studentEvaluation}
            textAreaStyles={{
              ...textAreaStyles.cellResponse,
              ...(isSplitView ? splitViewTextAreaStyles.cellResponse : {}),
              ...(showRevampedEvaluationUI
                ? textAreaStyles.cellResponseV2
                : {}),
            }}
            updateInputField={updateStudentEvaluation}
            textStyle={{
              ...(isSplitView && _.isEmpty(studentEvaluation)
                ? { color: colors.textDisabled }
                : {}),
            }}
            minRows={showRevampedEvaluationUI ? 3 : 1}
            emptyText={
              showRevampedEvaluationUI
                ? "-"
                : t("common:student_evaluation_pending")
            }
            mode={studentMode}
            className={
              showRevampedEvaluationUI
                ? classes.placeHolder
                : classes.placeholderColor
            }
            placeholder={t("common:typeHere")}
            updateInputRef={ref => {
              if (studentMode === "edit") {
                evaluationCellRefs[refKey] = ref;
              }
            }}
            onKeyDown={e => onKeyDown({ e, standardId, criteriaId })}
          />
        </div>
      )}
    </div>
  );
};

class SinglePointRubricTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inFocusRow: null,
      inFocusCell: null,
    };
    if (props.customRef) {
      props.customRef(this);
    }
    this.cellRefs = {};
    this.evaluationCellRefs = {};
    this.focusedCell = null;

    this.debouncedWriteSinglePointRubricElementLocal = _.debounce(
      props.writeSinglePointRubricElementLocal,
      1000
    );
  }

  blur = () => {
    if (this.focusedCell && this.cellRefs[this.focusedCell]) {
      this.cellRefs[this.focusedCell].blur();
    }
  };

  writeSinglePointRubricElement = ({
    id,
    type,
    params,
    elementDetails,
    isTitle,
  }) => {
    const { updateField, portalType } = this.props;
    this.debouncedWriteSinglePointRubricElementLocal({
      id,
      type,
      params,
      elementDetails,
    });
    updateField({
      key: `${id}_${isTitle ? "title" : "subTitle"}`,
      params: { id, type, params, portalType },
      type: "SINGLE_POINT_RUBRIC_ELEMENT",
    });
  };

  updateInputRef = ({ ref, refKey }) => {
    this.cellRefs[refKey] = ref;
  };

  setFocusedCell = refKey => {
    this.focusedCell = refKey;
  };

  getHeaderRow = () => {
    const {
      rubric,
      headerRowStyle = {},
      writeSinglePointRubricElementLocal,
    } = this.props;

    const { inFocusCell } = this.state;
    const criterias = _.get(rubric, "criterias", []);

    return (
      <div className={classes.pseudoRow}>
        <div className={classes.headerRow} style={headerRowStyle}>
          {_.map(criterias, (criteriaObj, idx) => {
            const isTopLeftCell = idx === 0;
            const isTopRightCell = idx === _.size(criterias) - 1;

            return (
              <Header
                {...this.props}
                criteriaObj={criteriaObj}
                inFocusCell={inFocusCell}
                updateInputRef={this.updateInputRef}
                setFocusedCell={this.setFocusedCell}
                writeSinglePointRubricElement={
                  this.writeSinglePointRubricElement
                }
                writeSinglePointRubricElementLocal={
                  writeSinglePointRubricElementLocal
                }
                saveSinglePointRubricUpdateActivityStatus={
                  this.saveSinglePointRubricUpdateActivityStatus
                }
                isTopLeftCell={isTopLeftCell}
                isTopRightCell={isTopRightCell}
              />
            );
          })}
        </div>
      </div>
    );
  };

  getRows = () => {
    const { rowStyle = {}, rubric, showUpdatedDesign = false } = this.props;

    const standards = _.get(rubric, "standards", []);

    const rowClasses = classNames({
      [classes.row]: true,
      [classes.rowV2]: showUpdatedDesign,
    });

    return _.map(standards, (standardObj, idx) => {
      const isLastRow = idx === _.size(standards) - 1;

      return (
        <div
          className={classes.pseudoRow}
          key={standardObj.id}
          onFocus={() => {
            this.setState({ inFocusRow: standardObj.id });
          }}
        >
          <div className={rowClasses} style={rowStyle}>
            {this.getColumns(standardObj, isLastRow)}
          </div>
        </div>
      );
    });
  };

  getColumns = (standardObj, isLastRow) => {
    const { rubric, writeSinglePointRubricElementLocal } = this.props;
    const { inFocusCell } = this.state;

    const criterias = _.get(rubric, "criterias", []);

    return _.map(criterias, (criteriaObj, idx) => {
      const isFirstColumn = idx === 0;
      const isLastColumn = idx === _.size(criterias) - 1;

      return (
        <Columns
          {...this.props}
          criteriaObj={criteriaObj}
          standardObj={standardObj}
          inFocusCell={inFocusCell}
          setFocusedCell={this.setFocusedCell}
          updateInputRef={this.updateInputRef}
          deleteSinglePointRubricStandard={this.deleteSinglePointRubricStandard}
          writeSinglePointRubricElement={this.writeSinglePointRubricElement}
          getNonCriteriaEvaluationCell={params =>
            this.getNonCriteriaEvaluationCell(params)
          }
          writeSinglePointRubricElementLocal={
            writeSinglePointRubricElementLocal
          }
          saveSinglePointRubricUpdateActivityStatus={
            this.saveSinglePointRubricUpdateActivityStatus
          }
          isFirstColumn={isFirstColumn}
          isLastColumn={isLastColumn}
          isLastRow={isLastRow}
        />
      );
    });
  };

  getNonCriteriaEvaluationCell = ({
    standardObj,
    criteriaObj,
    aiEvaluationText,
  }) => {
    return (
      <NonCriteriaEvaluationCell
        {...this.props}
        standardObj={standardObj}
        criteriaObj={criteriaObj}
        evaluationCellRefs={this.evaluationCellRefs}
        aiEvaluationText={aiEvaluationText}
      />
    );
  };

  createSinglePointRubricStandard = () => {
    const {
      rubric: { id: rubricId },
      createSinglePointRubricStandard,
      setScrollToBottom,
      portalType,
    } = this.props;
    createSinglePointRubricStandard({ rubricId, portalType });
    if (setScrollToBottom) {
      setTimeout(setScrollToBottom);
    }
  };

  deleteSinglePointRubricStandard = id => {
    const {
      rubric: { id: rubricId },
      deleteSinglePointRubricStandard,
      portalType,
    } = this.props;

    deleteSinglePointRubricStandard({ id, rubricId, portalType });
  };

  saveSinglePointRubricUpdateActivityStatus = () => {
    const { assignmentId, saveAssessmentToolUpdateStatusInRedux } = this.props;
    saveAssessmentToolUpdateStatusInRedux({
      assignmentId,
      toolType: "SINGLE_POINT_RUBRIC",
    });
  };

  renderActionButtons = () => {
    const {
      mode,
      onClickToolSave,
      t,
      showSaveTemplateButton,
      bottomContainerStyle = {},
      showActionButtons = true,
      actionButtonsUIType = "BUTTON",
      testId,
    } = this.props;

    if (mode === "view" || !showActionButtons) return null;

    const renderButtons = () => {
      switch (actionButtonsUIType) {
        case "BUTTON": {
          return (
            <>
              <Button
                dsVersion="2.0"
                size={"medium"}
                onClick={this.createSinglePointRubricStandard}
                variant="neutral"
                type="outlined"
                icon={<AddOutlined />}
                testId={`${testId}-singleRubricTable-createSinglePointRubricStandard`}
              >
                {t("common:add_with_label", { label: t("common:row") })}
              </Button>
              {showSaveTemplateButton ? (
                <Button
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  size={"medium"}
                  onClick={onClickToolSave}
                  testId={`${testId}-singleRubricTable-saveAsTemplate`}
                >
                  {t("classRoom:save_as_template")}
                </Button>
              ) : null}
            </>
          );
        }
        case "ICON_BUTTON": {
          return (
            <>
              <Tooltip
                placement={"top"}
                tooltip={t("common:add_with_label", {
                  label: t("common:row"),
                })}
                showArrow={true}
              >
                <IconButton
                  dsVersion="2.0"
                  icon={<SinglePointRubricOutlined variant="subtle" />}
                  variant="neutral"
                  type="plain"
                  size={"small"}
                  onClick={this.createSinglePointRubricStandard}
                  testId={`${testId}-singleRubricTable-createSinglePointRubricStandard`}
                />
              </Tooltip>

              {showSaveTemplateButton ? (
                <Tooltip
                  placement={"top"}
                  tooltip={t("classRoom:save_as_template")}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<AssessmentApplicableOutlined variant="subtle" />}
                    variant="neutral"
                    type="plain"
                    size={"small"}
                    onClick={onClickToolSave}
                    testId={`${testId}-singleRubricTable-saveAsTemplate`}
                  />
                </Tooltip>
              ) : null}
            </>
          );
        }
      }
    };

    return (
      <div className={classes.bottomContainer} style={bottomContainerStyle}>
        {renderButtons()}
      </div>
    );
  };

  render() {
    const { mode, rubric, containerStyle } = this.props;

    return (
      <React.Fragment>
        <div
          onBlur={() => {
            this.setState({ inFocusRow: null, inFocusCell: null });
          }}
          className={classes.container}
          style={
            mode == "view"
              ? { marginTop: 0, ...containerStyle }
              : { ...containerStyle }
          }
        >
          {rubric ? (
            <React.Fragment>
              {this.getHeaderRow()}
              {this.getRows()}
            </React.Fragment>
          ) : (
            <div className={classes.emptyText} style={{ padding: "16px" }}>
              {` No Single Point Rubric defined`}
            </div>
          )}
        </div>
        {this.renderActionButtons()}
      </React.Fragment>
    );
  }
}

const SinglePointRubricTableWrapper = props => {
  const { isEnabledAssessmentToolReordering = false, uiType, mode } = props;
  const { singlePointRubricStyles = {} } = getAssessmentToolStyles({ uiType });
  const SinglePointRubricTableComp =
    isEnabledAssessmentToolReordering &&
    (mode === "edit" || mode === "create") ? (
      <SinglePointRubricWithReordering {...props} />
    ) : (
      <SinglePointRubricTable {...props} {...singlePointRubricStyles} />
    );

  return SinglePointRubricTableComp;
};

const mapActionCreators = {
  updateField,
  writeSinglePointRubricElementLocal,
  createSinglePointRubricStandard,
  deleteSinglePointRubricStandard,
  updateEvaluationData,
  saveAssessmentToolUpdateStatusInRedux,
};

const mapStateToProps = state => {
  return {
    userType: state.login.userInfo.user_type,
  };
};

export default compose(
  I18nHOC({ resource: ["unitPlan", "common"] }),
  connect(mapStateToProps, mapActionCreators)
)(SinglePointRubricTableWrapper);

SinglePointRubricTableWrapper.defaultProps = {
  isEvaluation: false,
  isEvaluationEnable: false,
  toolEvaluationResponses: [],
  isEvaluatedByStudent: false,
  containerStyle: {},
  showSaveTemplateButton: true,
  testId: "appComponents",
};
